/**
 * AdtCorrespondants.jsx
 * Bloc en-tête Autorisation de Travail :
 * - Commande WorkOrder (texte libre)
 * - Service prescripteur (liste déroulante)
 * - Correspondant travaux Lafarge (depuis CoordonnéesLHC + correspondant_principal du site)
 * - Correspondant travaux ST (depuis représentants de l'entreprise extérieure sélectionnée)
 * - vCard QR Code pour numéro d'urgence du site (scannable smartphone)
 */
import { useMemo } from 'react';
import { Phone, User as UserIcon, Briefcase, QrCode, AlertOctagon } from 'lucide-react';
import Combobox from '@/components/ui/Combobox';

const SERVICES_PRESCRIPTEURS = [
  'GÉNÉRAL',
  'MAINTENANCE',
  'EXPLOITATION',
  'PRODUCTION',
  'T.NEUFS',
  'CARRIÈRE',
  'LABORATOIRE',
  'SÉCURITÉ',
  'ENVIRONNEMENT',
  'ÉLECTRIQUE',
  'MÉCANIQUE',
  'AUTRE',
];

function buildVCard({ site, emergency }) {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:Lafarge ${site?.name || ''};;;;`,
    `FN:Lafarge ${site?.name || ''} - URGENCES`,
    `ORG:Lafarge Ciments France`,
    site?.adresse ? `ADR:;;${site.adresse};;;;` : '',
    emergency ? `TEL;TYPE=WORK,VOICE:${emergency}` : '',
    site?.correspondant_principal_email ? `EMAIL;TYPE=WORK:${site.correspondant_principal_email}` : '',
    'END:VCARD',
  ].filter(Boolean);
  return lines.join('\n');
}

export default function AdtCorrespondants({
  extra,
  setExtra,
  site,                 // current site object from sites list
  lafargeContacts = [],
  selectedCompany,      // selected EE company object
}) {
  // ---- Build lookup helpers ----
  const lafargeOptions = useMemo(
    () => lafargeContacts.map((c) => ({
      value: c.name,
      label: c.name,
      sublabel: [c.fonction || c.service, c.portable].filter(Boolean).join(' • '),
      _data: c,
    })),
    [lafargeContacts]
  );

  // Représentants côté ST : récupère TOUS les contacts liés à l'entreprise sélectionnée
  // (selectedCompany contient déjà representant + rep_portable; on peut en ajouter d'autres si on enrichit le dataset)
  const stOptions = useMemo(() => {
    if (!selectedCompany) return [];
    const list = [];
    if (selectedCompany.representant) {
      list.push({
        value: selectedCompany.representant,
        label: selectedCompany.representant,
        sublabel: [selectedCompany.activite, selectedCompany.rep_portable].filter(Boolean).join(' • '),
        _data: {
          name: selectedCompany.representant,
          fonction: 'Représentant',
          portable: selectedCompany.rep_portable || selectedCompany.rep_fixe || '',
          email: selectedCompany.rep_mail || '',
        },
      });
    }
    return list;
  }, [selectedCompany]);

  const handleLafargePick = (name, opt) => {
    const c = opt?._data || lafargeContacts.find((x) => x.name === name);
    setExtra('corresp_lafarge_nom', name);
    if (c) {
      setExtra('corresp_lafarge_fonction', c.fonction || c.service || '');
      setExtra('corresp_lafarge_portable', c.portable || c.fixe || '');
      setExtra('corresp_lafarge_email', c.email || '');
    }
  };
  const handleStPick = (name, opt) => {
    const c = opt?._data;
    setExtra('corresp_st_nom', name);
    if (c) {
      setExtra('corresp_st_fonction', c.fonction || '');
      setExtra('corresp_st_portable', c.portable || '');
    }
  };

  // QR Code via quickchart.io (free, no auth) — encodes vCard
  const vcard = useMemo(() => buildVCard({ site, emergency: site?.emergency }), [site]);
  const qrUrl = site
    ? `https://quickchart.io/qr?text=${encodeURIComponent(vcard)}&size=200&margin=2&ecLevel=L`
    : null;

  return (
    <div className="space-y-5">
      {/* Header purple-violet style Lafarge sheet "Commande WorkOrder" + "Service prescripteur" */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label-industrial" style={{ color: '#7c3aed' }}>Commande WorkOrder</label>
          <input
            data-testid="adt-commande-workorder"
            type="text"
            className="input-industrial w-full bg-amber-50"
            value={extra.commande_workorder || ''}
            onChange={(e) => setExtra('commande_workorder', e.target.value)}
            placeholder="Ex. WO-2026-04521"
          />
        </div>
        <div>
          <label className="label-industrial" style={{ color: '#7c3aed' }}>Service prescripteur</label>
          <input
            data-testid="adt-service-prescripteur"
            type="text"
            list="service-prescripteur-options"
            className="input-industrial w-full bg-amber-50 font-bold text-[#09090B] uppercase"
            value={extra.service_prescripteur || ''}
            onChange={(e) => setExtra('service_prescripteur', e.target.value.toUpperCase())}
            placeholder="GÉNÉRAL"
          />
          <datalist id="service-prescripteur-options">
            {SERVICES_PRESCRIPTEURS.map((s) => <option key={s} value={s} />)}
          </datalist>
        </div>
      </div>

      {/* Correspondants + vCard */}
      <div className="border-2 border-stone-300 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 divide-x divide-stone-200">
          {/* Correspondant Lafarge */}
          <div className="lg:col-span-5 p-4 space-y-3">
            <div className="text-sm font-bold text-[#09090B] border-b border-stone-200 pb-2">
              Correspondant travaux Lafarge
            </div>
            <div>
              <label className="label-industrial flex items-center gap-1"><UserIcon className="w-3 h-3" /> Nom</label>
              <Combobox
                value={extra.corresp_lafarge_nom || ''}
                onChange={handleLafargePick}
                options={lafargeOptions}
                placeholder="LHOTELLIER PHILIPPE"
                testid="adt-corresp-lafarge-nom"
                className="input-industrial w-full bg-amber-50 font-bold text-blue-700 uppercase"
                emptyMessage="Aucun correspondant Lafarge trouvé"
              />
            </div>
            <div>
              <label className="label-industrial flex items-center gap-1"><Briefcase className="w-3 h-3" /> Fonction</label>
              <input
                data-testid="adt-corresp-lafarge-fonction"
                type="text"
                className="input-industrial w-full bg-amber-50 text-blue-700 uppercase"
                value={extra.corresp_lafarge_fonction || ''}
                onChange={(e) => setExtra('corresp_lafarge_fonction', e.target.value)}
                placeholder="RESP. SERVICE GÉNÉRAL"
              />
            </div>
            <div>
              <label className="label-industrial flex items-center gap-1"><Phone className="w-3 h-3" /> Portable</label>
              <input
                data-testid="adt-corresp-lafarge-portable"
                type="tel"
                className="input-industrial w-full bg-amber-50 text-blue-700 font-mono"
                value={extra.corresp_lafarge_portable || ''}
                onChange={(e) => setExtra('corresp_lafarge_portable', e.target.value)}
                placeholder="06.98.34.45.81"
              />
            </div>
          </div>

          {/* Correspondant ST */}
          <div className="lg:col-span-5 p-4 space-y-3">
            <div className="text-sm font-bold text-[#09090B] border-b border-stone-200 pb-2">
              Correspondant travaux EE
            </div>
            <div>
              <label className="label-industrial flex items-center gap-1"><UserIcon className="w-3 h-3" /> Nom</label>
              <Combobox
                value={extra.corresp_st_nom || ''}
                onChange={handleStPick}
                options={stOptions}
                placeholder={selectedCompany ? "Sélectionner un représentant" : "Sélectionnez d'abord une entreprise extérieure"}
                testid="adt-corresp-st-nom"
                className="input-industrial w-full bg-amber-50 font-bold text-blue-700 uppercase"
                emptyMessage={selectedCompany ? `Aucun contact référencé pour ${selectedCompany.name}` : "Sélectionnez d'abord l'entreprise extérieure (étape suivante)"}
              />
            </div>
            <div>
              <label className="label-industrial flex items-center gap-1"><Briefcase className="w-3 h-3" /> Fonction</label>
              <input
                data-testid="adt-corresp-st-fonction"
                type="text"
                className="input-industrial w-full bg-amber-50 text-blue-700"
                value={extra.corresp_st_fonction || ''}
                onChange={(e) => setExtra('corresp_st_fonction', e.target.value)}
                placeholder="Intervenant entreprise sous-traitante"
              />
            </div>
            <div>
              <label className="label-industrial flex items-center gap-1"><Phone className="w-3 h-3" /> Portable</label>
              <input
                data-testid="adt-corresp-st-portable"
                type="tel"
                className="input-industrial w-full bg-amber-50 text-blue-700 font-mono"
                value={extra.corresp_st_portable || ''}
                onChange={(e) => setExtra('corresp_st_portable', e.target.value)}
                placeholder="XX.XX.XX.XX.XX"
              />
            </div>
          </div>

          {/* vCard QR */}
          <div className="lg:col-span-2 p-3 bg-stone-50 flex flex-col items-center justify-center text-center">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#DA291C] mb-1.5 flex items-center gap-1">
              <AlertOctagon className="w-3 h-3" /> Urgences
            </div>
            <div className="text-[9px] uppercase font-bold text-stone-500 tracking-wider mb-2">vCard</div>
            {qrUrl ? (
              <a
                data-testid="adt-vcard-qr"
                href={`data:text/vcard;charset=utf-8,${encodeURIComponent(vcard)}`}
                download={`urgences-${site?.code || 'lafarge'}.vcf`}
                title="Cliquer pour télécharger la vCard, ou scanner le QR avec votre téléphone"
                className="block border-2 border-[#DA291C] p-1 bg-white hover:scale-105 transition-transform"
              >
                <img src={qrUrl} alt="QR Code Urgences" className="w-full h-auto max-w-[120px]" />
              </a>
            ) : (
              <div className="border-2 border-dashed border-stone-300 p-4 text-[10px] text-stone-400 italic">
                <QrCode className="w-8 h-8 mx-auto mb-1" />
                Sélectionnez un site
              </div>
            )}
            {site?.emergency && (
              <div className="mt-2 text-xs font-mono font-bold text-[#DA291C]">{site.emergency}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
